import {
  AccountBalance,
  Apartment,
  DeleteOutline,
  Gavel,
  LocationCity,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  useCreateAdminUnitMunicipality,
  useDeleteAdminUnitMunicipality,
} from '../../Aemter/hooks';
import { useAlert } from '../../context/AlertContext';
import type { ICourt } from '../../Interfaces/ICourt';
import type { IAuthority } from '../../Interfaces/IAuthority';
import type { ICourtRole } from '../../Interfaces/ICourtRole';
import type { ICourtAdminstrativeUnitAssignment } from '../../Interfaces/ICourtAdminstrativeUnitAssignment';
import type { IAdminstrativeUnit } from '../../Interfaces/IAdminstrativeUnit';
import type { IFederalState } from '../../Interfaces/IAemter';
import type {
  IMunicipality,
  IAdminUnitMunicipality,
} from '../../Interfaces/IMunicipality';

interface IProps {
  municipality: IMunicipality;
  federalStates: IFederalState[];
  administrativeUnits: IAdminstrativeUnit[];
  mappings: IAdminUnitMunicipality[];
  assignments: ICourtAdminstrativeUnitAssignment[];
  courts: ICourt[];
  authorities: IAuthority[];
  courtRoles: ICourtRole[];
  onAnyChange: () => void;
}

/**
 * Detail-Pane für die politische Gemeinde (#1207): Gemarkungen zuordnen und die
 * über diese Gemarkungen **aggregierten** Zuständigkeiten (Gericht/Behörde je
 * Rolle) ansehen. Die Zuordnungen selbst sind an der Gemarkung autoritativ und
 * werden dort bearbeitet – hier nur lesend zusammengeführt.
 */
export const MunicipalityDetailPane: React.FC<IProps> = ({
  municipality,
  federalStates,
  administrativeUnits,
  mappings,
  assignments,
  courts,
  authorities,
  courtRoles,
  onAnyChange,
}) => {
  const { showAlert } = useAlert();
  const { createAdminUnitMunicipality, isPendingCreateAdminUnitMunicipality } =
    useCreateAdminUnitMunicipality();
  const { deleteAdminUnitMunicipality } = useDeleteAdminUnitMunicipality();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerGemarkungId, setPickerGemarkungId] = useState<number | ''>('');

  const fsName =
    federalStates.find(f => f.idFederalState === municipality.idFederalState)
      ?.FederalState ?? '';

  const myMappings = mappings.filter(
    m =>
      m.idMunicipality === municipality.idMunicipality &&
      m.activeAssignment !== false
  );
  const mappedGemarkungIds = new Set(
    myMappings.map(m => m.idAdministrativeUnit)
  );

  const gemarkungName = (id: number) =>
    administrativeUnits.find(u => u.idAdministrativeUnit === id)
      ?.AdministrativeUnit ?? `Gemarkung ${id}`;

  // Zuordenbare Gemarkungen: gleiches Bundesland, noch nicht verknüpft.
  const addableGemarkungen = administrativeUnits
    .filter(
      u =>
        u.idFederalState === municipality.idFederalState &&
        !mappedGemarkungIds.has(u.idAdministrativeUnit)
    )
    .sort((a, b) => a.AdministrativeUnit.localeCompare(b.AdministrativeUnit));

  // Aggregierte Zuständigkeiten über die verknüpften Gemarkungen.
  const officeLabel = (a: ICourtAdminstrativeUnitAssignment): string => {
    if (a.idAuthority) {
      return (
        authorities.find(x => x.idAuthority === a.idAuthority)?.Authority ??
        'Behörde'
      );
    }
    return courts.find(x => x.idCourt === a.idCourt)?.Court ?? 'Gericht';
  };
  const isAuthorityAssignment = (a: ICourtAdminstrativeUnitAssignment) =>
    Boolean(a.idAuthority);

  const aggregated = assignments.filter(
    a =>
      mappedGemarkungIds.has(a.idAdministrativeUnit) &&
      a.activeAssignment !== false
  );
  // Gruppierung nach Rolle.
  const byRole = new Map<number, ICourtAdminstrativeUnitAssignment[]>();
  for (const a of aggregated) {
    const arr = byRole.get(a.idCourtRole) ?? [];
    arr.push(a);
    byRole.set(a.idCourtRole, arr);
  }
  const roleName = (id: number) =>
    courtRoles.find(r => r.idCourtRole === id)?.CourtRole ?? `Rolle ${id}`;

  const handleAddGemarkung = async (idAdministrativeUnit: number) => {
    try {
      await createAdminUnitMunicipality({
        idAdminUnitMunicipality: -1,
        idAdministrativeUnit,
        idMunicipality: municipality.idMunicipality,
        activeAssignment: true,
      });
      showAlert({ text: 'Gemarkung zugeordnet' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  const handleRemoveMapping = async (m: IAdminUnitMunicipality) => {
    try {
      await deleteAdminUnitMunicipality(m.idAdminUnitMunicipality);
      showAlert({ text: 'Zuordnung entfernt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  const submitPicker = async () => {
    if (typeof pickerGemarkungId !== 'number') return;
    await handleAddGemarkung(pickerGemarkungId);
    setPickerGemarkungId('');
    setPickerOpen(false);
  };

  return (
    <Box>
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction='row' spacing={1} alignItems='center'>
            <LocationCity color='primary' />
            <Box>
              <Typography variant='h6'>{municipality.Municipality}</Typography>
              <Typography variant='caption' color='text.secondary'>
                {fsName}
                {municipality.AGS ? ` · AGS ${municipality.AGS}` : ''}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Zugeordnete Gemarkungen */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 1 }}
          >
            <Typography variant='subtitle1' fontWeight={600}>
              Zugeordnete Gemarkungen
            </Typography>
            <Button
              size='small'
              variant='outlined'
              onClick={() => {
                setPickerGemarkungId('');
                setPickerOpen(true);
              }}
              disabled={addableGemarkungen.length === 0}
            >
              Gemarkung hinzufügen
            </Button>
          </Stack>
          {myMappings.length === 0 ? (
            <Alert severity='info' variant='outlined'>
              Noch keine Gemarkungen zugeordnet. Ordnen Sie Gemarkungen zu,
              damit die Zuständigkeiten aggregiert werden können.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {myMappings
                .slice()
                .sort((a, b) =>
                  gemarkungName(a.idAdministrativeUnit).localeCompare(
                    gemarkungName(b.idAdministrativeUnit)
                  )
                )
                .map(m => (
                  <Chip
                    key={m.idAdminUnitMunicipality}
                    icon={<Apartment />}
                    label={gemarkungName(m.idAdministrativeUnit)}
                    size='small'
                    variant='outlined'
                    onDelete={() => void handleRemoveMapping(m)}
                    deleteIcon={
                      <Tooltip title='Zuordnung entfernen'>
                        <DeleteOutline />
                      </Tooltip>
                    }
                  />
                ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Aggregierte Zuständigkeiten (read-only) */}
      <Card variant='outlined'>
        <CardContent>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
            Zuständigkeiten (über die zugeordneten Gemarkungen)
          </Typography>
          {byRole.size === 0 ? (
            <Alert severity='info' variant='outlined'>
              Keine Zuständigkeiten – entweder sind noch keine Gemarkungen
              zugeordnet oder für diese sind noch keine Stellen hinterlegt.
              Bearbeitet werden die Zuordnungen im Reiter „Gemarkung".
            </Alert>
          ) : (
            <Stack spacing={1}>
              {Array.from(byRole.entries())
                .sort((a, b) => roleName(a[0]).localeCompare(roleName(b[0])))
                .map(([roleId, list]) => (
                  <Box
                    key={roleId}
                    sx={{
                      p: 1.25,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant='body2'
                      fontWeight={600}
                      sx={{ mb: 0.5 }}
                    >
                      {roleName(roleId)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {list.map(a => (
                        <Chip
                          key={a.idAssignment}
                          icon={
                            isAuthorityAssignment(a) ? (
                              <AccountBalance />
                            ) : (
                              <Gavel />
                            )
                          }
                          label={`${officeLabel(a)} · ${gemarkungName(a.idAdministrativeUnit)}`}
                          size='small'
                          color='primary'
                          variant='outlined'
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Picker: Gemarkung hinzufügen */}
      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Gemarkung zuordnen</DialogTitle>
        <DialogContent>
          <TextField
            select
            label='Gemarkung'
            value={pickerGemarkungId === '' ? '' : String(pickerGemarkungId)}
            onChange={e => setPickerGemarkungId(Number(e.target.value))}
            fullWidth
            sx={{ mt: 1 }}
          >
            {addableGemarkungen.map(u => (
              <MenuItem
                key={u.idAdministrativeUnit}
                value={u.idAdministrativeUnit}
              >
                {u.AdministrativeUnit}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Abbruch</Button>
          <Button
            variant='contained'
            onClick={() => void submitPicker()}
            disabled={
              typeof pickerGemarkungId !== 'number' ||
              isPendingCreateAdminUnitMunicipality
            }
          >
            Zuordnen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
